/**
 * ExplodedView.jsx — the composed exploded-assembly page.
 *
 * Owns ALL shared state for the feature and wires the three parallel-built
 * pieces together:
 *
 *   AssemblyTree (left)  <-- selectedId / isolatedId / hiddenIds -->  ExplodedScene (centre)
 *                                              |
 *                          PartDetailPanel | PartDossierPanel (right)
 *
 * State model (single source of truth, this component):
 *   selectedId  string|null   the picked part; tree click <-> 3D click, both ways
 *   isolatedId  string|null   soloed subtree; everything else is dimmed
 *   hiddenIds   Set<string>   explicitly hidden subtrees
 *   t           number 0..1   explode parameter (damped inside the scene)
 *   showSkin    boolean       mount the manifest's decorative shell, when it has one
 *
 * Part knowledge (assets with knowledge: 'part' in config/assets.js, CONTRACT §4):
 *   dossier panel in the Details tab, fault trace from the fault legend, an ask
 *   bar with drill-down commands, answer/trace/coverage overlays. Legacy assets
 *   keep exactly the previous behaviour. Nothing 3D depends on the service.
 *
 * SPEC R5: the "Representative geometry — not OEM CAD" marker is rendered here,
 * in plain DOM, so it survives a WebGL failure, a Suspense fallback and an
 * empty manifest. The in-canvas copy is therefore switched off.
 *
 * Perf (Pi 4 / 4 GB): frameloop="demand". The scene self-invalidates while the
 * damped explode value is still moving; <DemandNudge> kicks the loop when a
 * prop the reconciler cannot see (t, showSkin, isolate...) changes. Pointer
 * hover is deliberately NOT lifted into React state — the scene highlights
 * hovered meshes internally, so hovering the viewport costs zero re-renders.
 * Knowledge overlays read part positions through partsApiRef in useFrame.
 *
 * Agent mode (CONTRACT §10.6), all optional; without them the view behaves as before:
 *   embedded     rendered inside the centre stage area instead of as a full-screen dialog; the
 *                tree column collapses to a toggle
 *   highlight    {ids, roles?, ranks?, colors?, label, seq}: a new seq switches to the 'agent'
 *                overlay, explodes to at least 60 %, frames the parts and solos the lead part
 *   actionQueue  [{action, seq}]: agent/voice ui.actions applied in order, each seq once, when the
 *                tree for the asset is ready
 */

import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';

import ExplodedScene from './ExplodedScene.jsx';
import CameraRig from './CameraRig.jsx';
import SceneLoadingOverlay from './SceneLoadingOverlay.jsx';
import { sceneLoad, modelUrlFor } from './sceneLoadStatus.js';
import AssemblyTree from './AssemblyTree.jsx';
import { PartDetailPanel } from './PartDetailPanel.jsx';
import { useAssembly, DEFAULT_VEHICLE } from './useAssembly.js';
import useDiagnostics from './useDiagnostics.js';
import DiagnosticLegend from './DiagnosticLegend.jsx';
import DraftingPanel from './DraftingPanel.jsx';
import { Info, DraftingCompass, Mic, ListTree } from 'lucide-react';
import {
  densityColor, statusFor, OVERLAY_MUTED, FOCUS_COLOR,
  traceRankColor, TRACE_CONTEXT, answerRole, coverageColor,
} from './diagnosticPalette.js';
import { EXPLODED_ASSETS, explodedAssetFor } from '../../config/assets.js';
import { faultSetFor } from '../../data/faultSets.js';
import { paintSubtrees, agentHighlightColors, leadPart } from './overlay.js';
import { pendingQueueItems } from '../agent/agentUi.js';
import { useParts } from './knowledge/usePartKnowledge.js';
import {
  parseCommand, planDrill, planResolved, DRILL_MIN_EXPLODE,
} from './knowledge/commands.js';
import { resolveLocally } from './knowledge/localResolve.js';
import PartDossierPanel from './knowledge/PartDossierPanel.jsx';
import TracePanel from './knowledge/TracePanel.jsx';
import AnswerCard from './knowledge/AnswerCard.jsx';
import AskBar from './knowledge/AskBar.jsx';
import FocusRig from './knowledge/FocusRig.jsx';
import { TraceLines, HopTrail } from './knowledge/KnowledgeOverlay3D.jsx';
import './ExplodedView.css';

/** SPEC R5 — must be on screen whenever this geometry is shown. */
export const PROVENANCE_TEXT = 'Representative geometry — not OEM CAD';

const EMPTY_HIDDEN = new Set();
const TRACE_MIN_EXPLODE = 0.6;
const ANSWER_MIN_EXPLODE = 0.6;
const HISTORY_MESSAGES = 6;

/**
 * frameloop="demand" only renders when something asks it to. R3F invalidates on
 * scene-graph prop writes, but `t` / `showSkin` / `isolatedId` are plain JS
 * values consumed inside effects, so nudge the loop explicitly.
 */
function DemandNudge({ signal }) {
  const invalidate = useThree((s) => s.invalidate);
  useEffect(() => { invalidate(); }, [signal, invalidate]);
  return null;
}

export default function ExplodedView({
  isVisible = true,
  onClose,
  /** {vehicle, t, selectedId} from the home stage, applied each time the view opens. */
  handoff = null,
  vehicle = DEFAULT_VEHICLE,
  className = '',
  /** Part ids the chat answer reasoned over — drives the focus overlay. */
  focusIds = null,
  /** Free text describing where focusIds came from, shown in the legend. */
  focusLabel = '',
  /**
   * Optional {id, seq}: open the fault trace for `id` once this asset's tree and
   * knowledge client are ready (voice "show the trace for …"). A new `seq`
   * re-fires the same id.
   */
  initialTrace = null,
  /**
   * Optional {action, seq}: a voice ui.action (select | explode | reset | deeper
   * | back) to apply while the view is open. A new `seq` applies it once.
   */
  voiceAction = null,
  /**
   * Optional ({vehicle, t, selectedId}) callback while the view is open, when
   * the selection or asset changes here, so the voice focus follows the screen.
   */
  onSelectionChange = null,
  /**
   * Optional: toggles the MHI Assistant voice panel, which stays on top of this view.
   * Without it there is no Voice button here (the home HUD is hidden behind the view).
   */
  onVoiceToggle = null,
  /** The voice panel is open (drives the Voice button's pressed state). */
  voiceOpen = false,
  /** Agent mode: render inside the centre stage (no aria-modal, collapsible tree). */
  embedded = false,
  /** Agent mode: {ids, roles?, ranks?, colors?, label, seq} from an agent view_highlight. */
  highlight = null,
  /** Agent mode: [{action, seq}] ui.actions to apply in order, each seq once. */
  actionQueue = null,
}) {
  const [activeVehicle, setActiveVehicle] = useState(vehicle);
  const asset = explodedAssetFor(activeVehicle);
  // Active faults are local demo data, so they work without the backend.
  const faultSet = faultSetFor(activeVehicle);
  const { manifest, tree, loading, error, reload, fetchNodeDetail } =
    useAssembly({ vehicle: activeVehicle });

  const [selectedId, setSelectedId] = useState(null);
  const [isolatedId, setIsolatedId] = useState(null);
  const [hiddenIds, setHiddenIds] = useState(EMPTY_HIDDEN);
  const [t, setT] = useState(0);
  const [showSkin, setShowSkin] = useState(false);
  // Overlays are mutually exclusive: 'none' | 'density' | 'dtc' | 'focus'
  // | 'coverage' | 'trace' | 'answer' | 'agent'. See diagnosticPalette.js for why.
  const [overlay, setOverlay] = useState('none');
  // The agent highlight the 'agent' overlay paints ({ids, roles, ranks, colors, label}).
  const [agentHl, setAgentHl] = useState(null);
  // Embedded (agent mode): the tree column is a toggle.
  const [treeOpen, setTreeOpen] = useState(false);
  // Right panel: 'details' (knowledge graph) or 'drafting' (ortho views).
  const [rightTab, setRightTab] = useState('details');
  // Bumped by the loading overlay's Retry to remount the scene after a failed
  // model load (the error boundary clears the cached rejection on unmount).
  const [sceneKey, setSceneKey] = useState(0);

  // Converted external models and the jet carry no legacy knowledge graph, so
  // the /node endpoint has no vehicle entry for them.
  // Only trust the flag once the manifest for THIS asset has loaded; the
  // previous asset's manifest lingers for a frame after switching.
  const hasKg = manifest?.vehicle === activeVehicle && manifest?.kg !== false;

  /* ---- part knowledge (knowledge: 'part') ---------------------------- */
  const knowledgeMode = asset.knowledge === 'part';
  const parts = useParts(activeVehicle, { enabled: isVisible && knowledgeMode, tree });
  const partsApiRef = useRef(null);

  const [trace, setTrace] = useState(null);
  const [answer, setAnswer] = useState(null);
  const [coverage, setCoverage] = useState({ asset: null, phase: 'idle', map: null, reason: null });
  const [hoverPartId, setHoverPartId] = useState(null);
  const [focusRequest, setFocusRequest] = useState(null);
  const [taskRequest, setTaskRequest] = useState(null);
  const [askStatus, setAskStatus] = useState(null);
  const [askBusy, setAskBusy] = useState(false);
  const prevOverlayRef = useRef('none');
  const traceAbortRef = useRef(null);
  const askAbortRef = useRef(null);
  const historyRef = useRef([]);
  const nonceRef = useRef(0);
  const coverageForRef = useRef(null);

  // Density comes from the legacy KG for legacy assets and from the part pack's
  // /bom for part-knowledge assets (the jet manifest has kg:false).
  const fetchBom = useCallback((signal) => parts.bom({ signal }), [parts]);
  const densityAvailable = hasKg || (knowledgeMode && Boolean(parts));

  // Only fetch diagnostics while the overlay is actually open.
  const {
    densityFor, densityError, dtcByNode, activeDtcs, unmapped, maxCount,
  } = useDiagnostics(activeVehicle, {
    enabled: isVisible && densityAvailable,
    faultSet,
    fetchBom: parts ? fetchBom : null,
    linkByPresence: knowledgeMode,
  });

  const focusSet = useMemo(
    () => new Set((focusIds || []).filter((id) => tree?.byId?.has(id))),
    [focusIds, tree],
  );

  const frame = useCallback((ids) => {
    if (!ids?.length) return;
    nonceRef.current += 1;
    setFocusRequest({ ids, nonce: nonceRef.current });
  }, []);

  /**
   * Assets with `isolateOnPick` (the pump: the thermostatic insert sits behind the cooling pack and the
   * cabinet units behind their back plate from the default view) solo a picked unit, so it is on
   * screen through its occluders, and frame it; the solo ends when the selection moves on. Used by
   * tree, trace-hypothesis and answer picks (handlePick) and by voice selections and highlights.
   */
  const pickIsolateRef = useRef(null);
  const soloPicked = useCallback((id) => {
    if (!asset.isolateOnPick || !id || tree.byId.get(id)?.level !== 'unit') return false;
    pickIsolateRef.current = id;
    setIsolatedId(id);
    setHiddenIds(EMPTY_HIDDEN);
    frame([id]);
    return true;
  }, [asset.isolateOnPick, tree, frame]);

  /** Enter a transient overlay, remembering what to restore on exit. */
  const enterOverlay = useCallback((mode) => {
    setOverlay((prev) => {
      if (prev !== 'trace' && prev !== 'answer') prevOverlayRef.current = prev;
      return mode;
    });
  }, []);

  /**
   * A new chat answer arrived: point the model at what it reasoned over.
   * Explodes to 60% so interior parts are actually visible, focuses the overlay
   * and selects the best match. Deliberately does NOT isolate — the user should
   * still see the highlighted parts in the context of the whole vehicle.
   */
  useEffect(() => {
    if (!focusIds || focusIds.length === 0) return;
    if (focusSet.size === 0) return;
    setOverlay('focus');
    setT((prev) => (prev < 0.6 ? 0.6 : prev));
    const best = focusIds.find((id) => focusSet.has(id)) ?? null;
    setSelectedId(best);
    setHiddenIds(EMPTY_HIDDEN);
    // Except where the lead part would be hidden behind its occluders (isolateOnPick assets):
    // there it is soloed like a pick, with the rest dimmed rather than removed.
    soloPicked(best);
  }, [focusIds, focusSet, soloPicked]);

  /* ---- fault trace ---------------------------------------------------- */
  const startTrace = useCallback((faultId) => {
    if (!parts) return;
    askAbortRef.current?.abort();
    setAnswer(null);
    setAskBusy(false);
    traceAbortRef.current?.abort();
    const controller = new AbortController();
    traceAbortRef.current = controller;

    const alert = (faultSet?.alerts ?? []).find((a) => a.id === faultId);
    const fallbackIds = (faultSet?.partMap?.[faultId] ?? []).filter((id) => tree.byId.has(id));
    setTrace({
      faultId,
      fault: alert ? { id: alert.id, title: alert.title, severity: alert.severity } : null,
      phase: 'loading',
      data: null,
      reason: null,
      fallbackIds,
    });
    enterOverlay('trace');
    setAskStatus(null);
    setIsolatedId(null);
    setHiddenIds(EMPTY_HIDDEN);
    setT((prev) => Math.max(prev, TRACE_MIN_EXPLODE));

    parts.trace(faultId, { signal: controller.signal }).then((res) => {
      if (res.reason === 'aborted') return;
      if (res.ok) {
        setTrace((tr) => (tr?.faultId === faultId ? { ...tr, phase: 'ready', data: res.data } : tr));
        const suspects = res.data.hypotheses.map((h) => h.cad_id).filter((id) => tree.byId.has(id));
        frame(suspects.length ? suspects : res.data.part_ids.filter((id) => tree.byId.has(id)));
        if (suspects[0]) setSelectedId(suspects[0]);
      } else {
        setTrace((tr) => (tr?.faultId === faultId ? { ...tr, phase: 'error', reason: res.reason } : tr));
        frame(fallbackIds);
        if (fallbackIds[0]) setSelectedId(fallbackIds[0]);
      }
    });
  }, [parts, faultSet, tree, enterOverlay, frame]);

  const exitTrace = useCallback(() => {
    traceAbortRef.current?.abort();
    setTrace(null);
    setHoverPartId(null);
    setOverlay(prevOverlayRef.current);
  }, []);

  const retryTrace = useCallback(() => {
    if (trace) startTrace(trace.faultId);
  }, [trace, startTrace]);

  const openTask = useCallback((cadId, taskId) => {
    setSelectedId(cadId);
    setRightTab('details');
    nonceRef.current += 1;
    setTaskRequest({ cadId, taskId, nonce: nonceRef.current });
  }, []);

  /* ---- ask + drill-down ---------------------------------------------- */
  const applyPlan = useCallback((plan, message) => {
    if (!plan) return;
    if (plan.select !== undefined) setSelectedId(plan.select);
    if (plan.isolate !== undefined) {
      setIsolatedId(plan.isolate);
      if (plan.isolate) setHiddenIds(EMPTY_HIDDEN);
    }
    if (plan.t !== undefined) setT((prev) => Math.max(prev, plan.t));
    if (plan.frame) frame(plan.frame);
    const text = message ?? plan.message;
    setAskStatus(text ? { text, tone: 'info' } : null);
  }, [frame]);

  const startAsk = useCallback((question, focusId) => {
    if (!parts) return;
    traceAbortRef.current?.abort();
    setTrace(null);
    askAbortRef.current?.abort();
    const controller = new AbortController();
    askAbortRef.current = controller;

    setAnswer({ question, focusId, phase: 'loading', data: null, reason: null });
    enterOverlay('answer');
    setAskBusy(true);
    setAskStatus(null);

    parts.ask(
      { question, focus_part_id: focusId ?? null, history: historyRef.current.slice(-HISTORY_MESSAGES) },
      { signal: controller.signal },
    ).then((res) => {
      if (res.reason === 'aborted') return;
      setAskBusy(false);
      if (!res.ok) {
        setAnswer((a) => (a?.question === question ? { ...a, phase: 'error', reason: res.reason } : a));
        return;
      }
      setAnswer((a) => (a?.question === question ? { ...a, phase: 'ready', data: res.data } : a));
      historyRef.current = historyRef.current
        .concat({ role: 'user', content: question }, { role: 'assistant', content: res.data.answer })
        .slice(-HISTORY_MESSAGES);
      const ids = [...new Set([
        ...res.data.part_ids.map((p) => p.cad_id),
        ...res.data.hops.map((h) => h.cad_id),
      ])].filter((id) => tree.byId.has(id));
      if (ids.length) {
        setIsolatedId(null);
        setT((prev) => Math.max(prev, ANSWER_MIN_EXPLODE));
        frame(ids);
      }
    });
  }, [parts, tree, enterOverlay, frame]);

  const closeAnswer = useCallback(() => {
    askAbortRef.current?.abort();
    setAnswer(null);
    setAskBusy(false);
    setHoverPartId(null);
    setOverlay(prevOverlayRef.current);
  }, []);

  const retryAnswer = useCallback(() => {
    if (answer) startAsk(answer.question, answer.focusId);
  }, [answer, startAsk]);

  const resetView = useCallback(() => {
    setT(0);
    setIsolatedId(null);
    setHiddenIds(EMPTY_HIDDEN);
    controlsRef.current?.reset?.();
  }, []);

  /* ---- home stage handoff: in on open, out on close ------------------- */
  const handoffRef = useRef(null);
  const pendingSelectionRef = useRef(undefined);
  const wasVisibleRef = useRef(false);
  useEffect(() => {
    const opened = isVisible && !wasVisibleRef.current;
    wasVisibleRef.current = isVisible;
    if (!opened || !handoff) return;
    if (handoff.vehicle && handoff.vehicle !== activeVehicle) {
      handoffRef.current = handoff;
      setActiveVehicle(handoff.vehicle);
    } else {
      setT(handoff.t ?? 0);
      setSelectedId(handoff.selectedId ?? null);
    }
  }, [isVisible, handoff, activeVehicle]);

  const closeView = useCallback(() => {
    if (typeof onClose === 'function') onClose({ vehicle: activeVehicle, t, selectedId });
  }, [onClose, activeVehicle, t, selectedId]);

  /** Back to the plain assembly: no trace, answer, overlay, selection or explode. */
  const clearAll = useCallback(() => {
    traceAbortRef.current?.abort();
    askAbortRef.current?.abort();
    setTrace(null);
    setAnswer(null);
    setAskBusy(false);
    setOverlay('none');
    setSelectedId(null);
    resetView();
  }, [resetView]);

  const handleAsk = useCallback((text) => {
    const cmd = parseCommand(text);
    if (cmd.type === 'empty') return;

    if (cmd.type === 'reset') {
      clearAll();
      setAskStatus({ text: 'View reset', tone: 'info' });
      return;
    }

    if (cmd.type === 'deeper' || cmd.type === 'up') {
      applyPlan(planDrill(cmd, { tree, isolatedId, selectedId, t }));
      return;
    }

    if (cmd.type === 'resolve') {
      setAskBusy(true);
      setAskStatus(null);
      const controller = new AbortController();
      askAbortRef.current?.abort();
      askAbortRef.current = controller;
      parts.resolve(cmd.text, { signal: controller.signal }).then((res) => {
        if (res.reason === 'aborted') return;
        setAskBusy(false);
        // Service down: the geometry still knows every part name.
        const local = !res.ok;
        const matches = res.ok ? res.data.matches
          : resolveLocally(cmd.query, tree, 5, { phrases: asset.resolverPhrases });
        const plan = planResolved(matches, { tree, t: 0 });
        if (!plan) {
          setAskStatus({ text: `No part matched “${cmd.query}”`, tone: 'warn' });
          return;
        }
        const name = tree.byId.get(plan.select)?.name ?? plan.select;
        applyPlan(plan, local ? `Opened ${name} (matched locally, knowledge service unavailable)` : `Opened ${name}`);
      });
      return;
    }

    startAsk(cmd.question, selectedId);
  }, [tree, isolatedId, selectedId, t, parts, applyPlan, startAsk, clearAll, asset]);

  /* ---- coverage ------------------------------------------------------- */
  useEffect(() => {
    if (overlay !== 'coverage' || !parts) return undefined;
    if (coverageForRef.current === activeVehicle) return undefined;
    const controller = new AbortController();
    const forAsset = activeVehicle;
    setCoverage({ asset: forAsset, phase: 'loading', map: null, reason: null });
    parts.coverage({ signal: controller.signal }).then((res) => {
      if (res.reason === 'aborted') return;
      if (res.ok) {
        coverageForRef.current = forAsset;
        setCoverage({
          asset: forAsset,
          phase: 'ready',
          map: new Map(res.data.nodes.map((n) => [n.id, Number(n.score) || 0])),
          mean: Number.isFinite(Number(res.data.mean_score)) ? Number(res.data.mean_score) : null,
          definition: typeof res.data.definition === 'string' ? res.data.definition : '',
          reason: null,
        });
      } else {
        setCoverage({ asset: forAsset, phase: 'error', map: null, reason: res.reason });
      }
    });
    return () => controller.abort();
  }, [overlay, parts, activeVehicle]);

  const chooseOverlay = useCallback((key) => {
    if (trace) { traceAbortRef.current?.abort(); setTrace(null); }
    if (answer) { askAbortRef.current?.abort(); setAnswer(null); setAskBusy(false); }
    setHoverPartId(null);
    setOverlay(key);
  }, [trace, answer]);

  /**
   * node id -> fill colour for the active overlay, or null when no overlay is
   * on (the scene then falls back to each part's identity colour).
   * Recomputed only when the overlay or its data changes — never per frame.
   */
  const overlayColors = useMemo(() => {
    if (overlay === 'none' || !tree) return null;

    if (overlay === 'trace') {
      if (!trace) return null;
      if (trace.phase === 'ready' && trace.data) {
        const entries = trace.data.hypotheses.map((h) => [h.cad_id, traceRankColor(h.rank)]);
        for (const id of trace.data.part_ids) entries.push([id, TRACE_CONTEXT]);
        return paintSubtrees(tree, entries);
      }
      return paintSubtrees(tree, trace.fallbackIds.map((id) => [id, traceRankColor(1)]));
    }

    if (overlay === 'answer') {
      if (answer?.phase !== 'ready' || !answer.data) return null;
      const entries = answer.data.part_ids.map((p) => [p.cad_id, answerRole(p.role).color]);
      for (const h of answer.data.hops) entries.push([h.cad_id, answerRole('context').color]);
      return entries.length ? paintSubtrees(tree, entries) : null;
    }

    if (overlay === 'agent') {
      return agentHl ? agentHighlightColors(tree, agentHl) : null;
    }

    if (overlay === 'coverage') {
      if (coverage.asset !== activeVehicle || !coverage.map) return null;
      const map = new Map();
      for (const id of tree.byId.keys()) map.set(id, coverageColor(coverage.map.get(id)));
      return map;
    }

    const map = new Map();
    for (const id of tree.byId.keys()) {
      if (overlay === 'density') {
        const d = densityFor(id);
        map.set(id, densityColor(d ? d.count : null, d ? d.linked : false));
      } else if (overlay === 'focus') {
        map.set(id, focusSet.has(id) ? FOCUS_COLOR : OVERLAY_MUTED);
      } else {
        const hit = dtcByNode.get(id);
        map.set(id, hit ? statusFor(hit.severity).color : OVERLAY_MUTED);
      }
    }
    return map;
  }, [overlay, tree, densityFor, dtcByNode, focusSet, trace, answer, coverage, activeVehicle, agentHl]);

  // Switching vehicle invalidates every id in flight.
  useEffect(() => {
    traceAbortRef.current?.abort();
    askAbortRef.current?.abort();
    historyRef.current = [];
    setSelectedId(null);
    setIsolatedId(null);
    setHiddenIds(EMPTY_HIDDEN);
    setOverlay('none');
    prevOverlayRef.current = 'none';
    setAgentHl(null);
    setTrace(null);
    setAnswer(null);
    setAskBusy(false);
    setAskStatus(null);
    setHoverPartId(null);
    setTaskRequest(null);
    setFocusRequest(null);
    setT(0);
    // A handoff that switched the asset lands here, after the reset above.
    const h = handoffRef.current;
    if (h && h.vehicle === activeVehicle) {
      handoffRef.current = null;
      setT(h.t ?? 0);
      pendingSelectionRef.current = h.selectedId ?? undefined;
    }
  }, [activeVehicle]);

  // Apply a handed-off selection once THIS asset's tree has loaded; earlier, the
  // stale-selection guard would drop it against the previous asset's tree.
  useEffect(() => {
    const id = pendingSelectionRef.current;
    if (id === undefined || manifest?.vehicle !== activeVehicle || !tree.byId.size) return;
    pendingSelectionRef.current = undefined;
    if (tree.byId.has(id)) setSelectedId(id);
  }, [tree, manifest, activeVehicle]);

  // Requested trace (initialTrace). Held in a ref and fired once this asset's
  // tree and parts client are ready: the [activeVehicle] reset above clears the
  // trace, and startTrace is a no-op without `parts`, so a plain prop effect
  // would be wiped or do nothing. Declared after the reset on purpose.
  const pendingTraceRef = useRef(null);
  const initialTraceKey = initialTrace?.id ? `${initialTrace.id}|${initialTrace.seq ?? ''}` : null;
  useEffect(() => {
    if (initialTraceKey) pendingTraceRef.current = initialTraceKey.split('|')[0];
  }, [initialTraceKey]);
  useEffect(() => {
    const id = pendingTraceRef.current;
    // Opening the workspace may still be switching to the stage's asset (handoff);
    // wait for it, or the previous asset's knowledgeMode would drop the request.
    if (!id || handoffRef.current || manifest?.vehicle !== activeVehicle || !tree.byId.size) return;
    // Geometry-only assets have no trace; drop the request rather than keep it
    // for whichever asset is opened next.
    if (!knowledgeMode) { pendingTraceRef.current = null; return; }
    if (!parts) return;
    pendingTraceRef.current = null;
    startTrace(id);
  }, [initialTraceKey, manifest, tree, activeVehicle, knowledgeMode, parts, startTrace]);

  /* ---- voice and agent ui.actions while the view is open ------------- */
  /** An agent highlight: 'agent' overlay, t >= 0.6, frame the parts, select and solo the lead part. */
  const applyHighlight = useCallback((h) => {
    const ids = (h?.ids ?? h?.part_ids ?? []).filter((id) => tree.byId.has(id));
    if (!ids.length) return;
    traceAbortRef.current?.abort();
    askAbortRef.current?.abort();
    setTrace(null);
    setAnswer(null);
    setAskBusy(false);
    setHoverPartId(null);
    setAgentHl({
      ids, roles: h.roles ?? null, ranks: h.ranks ?? null, colors: h.colors ?? null,
      label: h.label ?? h.caption ?? '',
    });
    setOverlay('agent');
    const minT = Math.max(TRACE_MIN_EXPLODE, typeof h.t === 'number' ? h.t : 0);
    setT((prev) => Math.max(prev, minT));
    setIsolatedId(null);
    setHiddenIds(EMPTY_HIDDEN);
    frame(ids);
    const lead = leadPart({ part_ids: ids, roles: h.roles, ranks: h.ranks });
    setSelectedId(lead);
    soloPicked(lead);
  }, [tree, frame, soloPicked]);

  /**
   * Apply one ui.action. `legacy` (the voiceAction prop) keeps exactly the five types it always
   * handled. Returns true when the action switched the asset, so queued actions after it wait for
   * the new tree.
   */
  const applyUiAction = useCallback((a, { legacy = false } = {}) => {
    switch (a?.type) {
      case 'select': {
        const id = (a.part_ids || []).find((p) => tree.byId.has(p));
        if (!id) break;
        setSelectedId(id);
        setT((prev) => Math.max(prev, typeof a.t === 'number' ? a.t : DRILL_MIN_EXPLODE));
        if (a.isolate && !legacy) {
          setIsolatedId(id);
          setHiddenIds(EMPTY_HIDDEN);
          frame([id]);
        } else if (!soloPicked(id)) frame([id]);
        break;
      }
      case 'explode':
        if (typeof a.t === 'number') setT(Math.min(1, Math.max(0, a.t)));
        break;
      case 'reset':
        clearAll();
        break;
      case 'deeper':
      case 'back':
        applyPlan(planDrill({ type: a.type === 'back' ? 'up' : 'deeper' }, { tree, isolatedId, selectedId, t }));
        break;
      case 'highlight':
        if (!legacy) applyHighlight({ ids: a.part_ids, roles: a.roles, ranks: a.ranks, colors: a.colors, t: a.t });
        break;
      case 'trace':
        if (!legacy && knowledgeMode && parts && a.fault_id) startTrace(a.fault_id);
        break;
      case 'asset':
        if (!legacy && a.asset && a.asset !== activeVehicle && EXPLODED_ASSETS.some((v) => v.id === a.asset)) {
          setActiveVehicle(a.asset);
          return true;
        }
        break;
      default:
        break;
    }
    return false;
  }, [tree, isolatedId, selectedId, t, frame, soloPicked, clearAll, applyPlan, applyHighlight, knowledgeMode, parts,
    startTrace, activeVehicle]);

  // Held until this asset's tree is in; each seq applies once.
  const treeReady = isVisible && !handoffRef.current && manifest?.vehicle === activeVehicle && tree.byId.size > 0;
  const voiceSeqRef = useRef(null);
  useEffect(() => {
    if (!voiceAction || voiceAction.seq === voiceSeqRef.current) return;
    if (!treeReady) return;
    voiceSeqRef.current = voiceAction.seq;
    applyUiAction(voiceAction.action || {}, { legacy: true });
  }, [voiceAction, treeReady, applyUiAction]);

  // Agent highlight prop: a new seq repaints once the tree is ready.
  const highlightSeqRef = useRef(null);
  useEffect(() => {
    if (!highlight || highlight.seq === highlightSeqRef.current) return;
    if (!treeReady) return;
    highlightSeqRef.current = highlight.seq;
    applyHighlight(highlight);
  }, [highlight, treeReady, applyHighlight]);

  // Agent action queue: every item newer than the last consumed seq, in order. An asset switch
  // stops the pass; the rest apply when the new tree is ready.
  const queueSeqRef = useRef(null);
  useEffect(() => {
    if (!Array.isArray(actionQueue) || actionQueue.length === 0 || !treeReady) return;
    for (const item of pendingQueueItems(actionQueue, queueSeqRef.current)) {
      queueSeqRef.current = item.seq;
      if (applyUiAction(item.action)) break;
    }
  }, [actionQueue, treeReady, applyUiAction]);

  // The view -> Dashboard while open, so the voice focus is the part on screen
  // (clicks, trace suspects, voice selections). Waits for a handed-off asset and
  // selection to land; the first report after opening is the stage's own state.
  const reportedRef = useRef(null);
  useEffect(() => {
    if (!isVisible) { reportedRef.current = null; return; }
    if (typeof onSelectionChange !== 'function' || handoffRef.current
      || pendingSelectionRef.current !== undefined || manifest?.vehicle !== activeVehicle) return;
    const key = `${activeVehicle}|${selectedId ?? ''}`;
    if (reportedRef.current === null) { reportedRef.current = key; return; }
    if (reportedRef.current === key) return;
    reportedRef.current = key;
    onSelectionChange({ vehicle: activeVehicle, t, selectedId });
  }, [isVisible, onSelectionChange, manifest, activeVehicle, selectedId, t]);

  const controlsRef = useRef(null);

  /* ---- selection: the bidirectional link ---------------------------- */
  const handleSelect = useCallback((id) => {
    setSelectedId((prev) => (id === prev ? prev : (id ?? null)));
  }, []);

  const handleIsolate = useCallback((id) => {
    setIsolatedId(id ?? null);
    if (id) setSelectedId(id);
  }, []);

  /** A part picked from the tree, a trace hypothesis or an answer's part list (soloPicked). */
  const handlePick = useCallback((id) => {
    handleSelect(id);
    soloPicked(id);
  }, [handleSelect, soloPicked]);
  useEffect(() => {
    const picked = pickIsolateRef.current;
    if (picked && selectedId !== picked) {
      pickIsolateRef.current = null;
      setIsolatedId((cur) => (cur === picked ? null : cur));
    }
  }, [selectedId]);

  const handleToggleVisible = useCallback((id, nextVisible) => {
    setHiddenIds((prev) => {
      const next = new Set(prev);
      if (nextVisible) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const showAll = useCallback(() => setHiddenIds(EMPTY_HIDDEN), []);

  // Stable identity so the memoised PartDetailPanel is not re-rendered by every
  // explode-slider tick.
  const clearSelection = useCallback(() => setSelectedId(null), []);

  /* ---- escape: trace, then answer, then the view itself -------------- */
  useEffect(() => {
    if (!isVisible) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      // Esc in a non-empty text field clears the field first (AskBar).
      const el = e.target;
      if (el && el.tagName === 'INPUT' && el.type === 'text' && el.value) return;
      // Agent cards and panels (data-esc-guard) handle their own Escape.
      if (el?.closest?.('[data-esc-guard]')) return;
      if (trace) { exitTrace(); return; }
      if (answer) { closeAnswer(); return; }
      closeView();
    };
    // Capture phase: a document-level keydown listener elsewhere in the app
    // stops propagation of trusted Escape presses before they bubble to window.
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [isVisible, closeView, trace, answer, exitTrace, closeAnswer]);

  // Drop a stale selection when a reload changes the manifest under us.
  useEffect(() => {
    if (selectedId && tree.byId.size && !tree.byId.has(selectedId)) setSelectedId(null);
    if (isolatedId && tree.byId.size && !tree.byId.has(isolatedId)) setIsolatedId(null);
  }, [tree, selectedId, isolatedId]);

  const nudge = useMemo(
    () => `${t}|${showSkin}|${isolatedId}|${selectedId}|${hiddenIds.size}|${overlay}`,
    [t, showSkin, isolatedId, selectedId, hiddenIds, overlay],
  );

  const knownHops = useMemo(
    () => (answer?.data?.hops ?? []).filter((h) => tree.byId.has(h.cad_id)),
    [answer, tree],
  );

  const partCount = tree.byId.size;
  const selectedName = selectedId ? tree.byId.get(selectedId)?.name : null;
  const showCard = knowledgeMode && (trace || answer);
  const legendMode = overlay === 'trace' || overlay === 'answer' ? 'none' : overlay;
  const rootClass = `exview${embedded ? ' exview--embedded' : ''}${embedded && treeOpen ? ' is-tree-open' : ''} ${className}`.trim();

  if (!isVisible) return null;

  return (
    <div
      className={rootClass}
      role={embedded ? 'region' : 'dialog'}
      aria-modal={embedded ? undefined : 'true'}
      aria-label="Exploded assembly view"
    >
      <header className="exview__bar">
        <div className="exview__brand">
          <h2 className="exview__title">Exploded assembly</h2>
          <span className="exview__sub">
            {asset.label}
            {partCount > 0 && <> · {partCount} parts</>}
          </span>
        </div>

        {/* SPEC R5 — persistent, always-visible provenance marker. */}
        <div className="exview__provenance" role="note" title="SPEC R5">
          <span className="exview__provenance-dot" aria-hidden="true" />
          {PROVENANCE_TEXT}
        </div>

        <div className="exview__tools">
          {embedded && (
            <button
              type="button"
              className={`exview__btn${treeOpen ? ' is-on' : ''}`}
              aria-pressed={treeOpen}
              aria-label={treeOpen ? 'Hide the assembly tree' : 'Show the assembly tree'}
              title="Assembly tree"
              onClick={() => setTreeOpen((v) => !v)}
            >
              <ListTree size={14} aria-hidden="true" />
            </button>
          )}
          <label className="exview__slider" htmlFor="exview-explode">
            <span className="exview__slider-label">Explode</span>
            <input
              id="exview-explode"
              className="exview__range"
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={t}
              onChange={(e) => setT(Number(e.target.value))}
              aria-valuetext={`${Math.round(t * 100)} percent`}
            />
            <output className="exview__slider-value" htmlFor="exview-explode">
              {Math.round(t * 100)}%
            </output>
          </label>

          <div className="exview__modes" role="group" aria-label="Asset">
            {EXPLODED_ASSETS.map((v) => (
              <button
                key={v.id}
                type="button"
                className={`exview__btn exview__btn--mode${activeVehicle === v.id ? ' is-on' : ''}`}
                aria-pressed={activeVehicle === v.id}
                title={v.note}
                onClick={() => setActiveVehicle(v.id)}
              >
                {v.label}
              </button>
            ))}
          </div>

          <div className="exview__modes" role="group" aria-label="Diagnostic overlay">
            {[
              ['none', 'None', 'Part identity colours'],
              ['density', 'Failure density',
                densityAvailable ? 'Recorded failure cases per part'
                  : 'No knowledge graph for this model'],
              ['dtc', faultSet?.label ?? 'Active faults',
                faultSet ? (knowledgeMode ? 'Live fault messages — click one to trace it'
                  : 'Live fault codes located on the model')
                  : 'No fault data for this model'],
              ...(knowledgeMode
                ? [['coverage', 'Knowledge coverage', 'How much of each part the knowledge pack documents']]
                : []),
              ...(focusSet.size > 0
                ? [['focus', 'From answer', 'Parts the last chat answer reasoned over']]
                : []),
              ...(agentHl
                ? [['agent', 'From agent', 'Parts the specialist highlighted']]
                : []),
            ].map(([key, label, title]) => (
              <button
                key={key}
                type="button"
                className={`exview__btn exview__btn--mode${overlay === key ? ' is-on' : ''}`}
                aria-pressed={overlay === key}
                title={title}
                disabled={(key === 'density' && !densityAvailable) || (key === 'dtc' && !faultSet)}
                onClick={() => chooseOverlay(key)}
              >
                {label}
                {key === 'dtc' && activeDtcs.length > 0 && (
                  <span className="exview__badge">{activeDtcs.length}</span>
                )}
              </button>
            ))}
          </div>

          {manifest?.skin?.model && (
            <button
              type="button"
              className={`exview__btn${showSkin ? ' is-on' : ''}`}
              aria-pressed={showSkin}
              title="Decorative body shell — not to scale (SPEC §4.1)"
              onClick={() => setShowSkin((v) => !v)}
            >
              Ghost skin
            </button>
          )}

          {hiddenIds.size > 0 && (
            <button type="button" className="exview__btn is-warn" onClick={showAll}>
              Show all ({hiddenIds.size})
            </button>
          )}

          {typeof onVoiceToggle === 'function' && (
            <button
              type="button"
              className={`exview__btn exview__btn--voice${voiceOpen ? ' is-on' : ''}`}
              aria-pressed={voiceOpen}
              title={voiceOpen ? 'Close the voice assistant' : 'Talk to the MHI Assistant about this assembly'}
              onClick={onVoiceToggle}
            >
              <Mic size={14} aria-hidden="true" />
              Voice
            </button>
          )}

          <button type="button" className="exview__btn" onClick={resetView}>
            Reset view
          </button>

          {typeof onClose === 'function' && (
            <button
              type="button"
              className="exview__btn exview__btn--close"
              onClick={closeView}
              aria-label="Close exploded view"
            >
              ✕
            </button>
          )}
        </div>
      </header>

      <div className="exview__body">
        <div className="exview__left">
          <AssemblyTree
            tree={tree}
            selectedId={selectedId}
            isolatedId={isolatedId}
            hiddenIds={hiddenIds}
            onSelect={handlePick}
            onIsolate={handleIsolate}
            onToggleVisible={handleToggleVisible}
          />
        </div>

        <div className={`exview__stage${knowledgeMode ? ' exview__stage--knowledge' : ''}`}>
          <Canvas
            className="exview__canvas"
            frameloop="demand"
            dpr={[1, 1.75]}
            camera={{ position: asset.camera.position, fov: 42, near: 0.05, far: asset.camera.far }}
            gl={{ antialias: true, powerPreference: 'high-performance' }}
          >
            <DemandNudge signal={nudge} />
            <OrbitControls
              ref={controlsRef}
              makeDefault
              target={asset.camera.target}
              enableDamping
              dampingFactor={0.08}
              minDistance={1.2}
              maxDistance={asset.camera.maxDistance}
              maxPolarAngle={Math.PI * 0.495}
            />
            <CameraRig config={asset.camera} controlsRef={controlsRef} />
            {knowledgeMode && (
              <FocusRig
                request={focusRequest}
                partsApiRef={partsApiRef}
                controlsRef={controlsRef}
                minDistance={1.2}
                maxDistance={asset.camera.maxDistance}
                insetRight={showCard ? 390 : 0}
              />
            )}
            <ExplodedScene
              key={sceneKey}
              tree={tree}
              manifest={manifest}
              t={t}
              selectedId={selectedId}
              isolatedId={isolatedId}
              hiddenIds={hiddenIds}
              showSkin={showSkin}
              isolateMode="dim"
              showProvenance={false}
              onSelect={handleSelect}
              overlayColors={overlayColors}
              highlightId={hoverPartId}
              partsApiRef={partsApiRef}
            />
            {knowledgeMode && overlay === 'trace' && trace?.data?.paths?.length > 0 && (
              <TraceLines paths={trace.data.paths} partsApiRef={partsApiRef} />
            )}
            {knowledgeMode && overlay === 'answer' && knownHops.length > 0 && (
              <HopTrail hops={knownHops} partsApiRef={partsApiRef} />
            )}
          </Canvas>

          {legendMode !== 'none' && (
            <div className="exview__legend">
              <DiagnosticLegend
                mode={legendMode}
                dtcTitle={faultSet?.title}
                focusCount={focusSet.size}
                focusLabel={focusLabel}
                maxCount={maxCount}
                densityError={densityError}
                activeDtcs={activeDtcs}
                unmapped={knowledgeMode ? [] : unmapped}
                dtcByNode={dtcByNode}
                onSelect={handleSelect}
                selectedId={selectedId}
                onTrace={knowledgeMode && parts ? startTrace : null}
                coverageError={coverage.phase === 'error' ? coverage.reason : null}
                coverageLoading={coverage.phase === 'loading'}
                coverageMean={coverage.mean ?? null}
                coverageDefinition={coverage.definition ?? ''}
                agentHighlight={agentHl}
                tree={tree}
              />
            </div>
          )}

          {showCard && (
            <div className="exview__kcard">
              {trace ? (
                <TracePanel
                  trace={trace}
                  tree={tree}
                  onSelect={handlePick}
                  onHover={setHoverPartId}
                  onOpenTask={openTask}
                  onRetry={retryTrace}
                  onExit={exitTrace}
                  domain={asset.domain}
                  knowledgeNote={asset.knowledgeNote}
                />
              ) : (
                <AnswerCard
                  answer={answer}
                  tree={tree}
                  onSelect={handlePick}
                  onHover={setHoverPartId}
                  onRetry={retryAnswer}
                  onClose={closeAnswer}
                />
              )}
            </div>
          )}

          {knowledgeMode && parts && (
            <div className="exview__ask">
              <AskBar
                placeholder={`Ask about ${selectedName ?? asset.label}`}
                onSubmit={handleAsk}
                busy={askBusy}
                status={askStatus}
              />
            </div>
          )}

          {!error && (loading || partCount > 0) && (
            <SceneLoadingOverlay
              assetLabel={asset.label}
              // The previous asset's manifest lingers until the fetch starts.
              manifestLoading={loading || manifest?.vehicle !== activeVehicle}
              modelUrl={manifest ? modelUrlFor(manifest) : null}
              partCount={partCount}
              onRetry={() => {
                if (manifest) sceneLoad.reset(modelUrlFor(manifest));
                setSceneKey((k) => k + 1);
              }}
            />
          )}
          {!loading && error && (
            <div className="exview__stage-note is-error" role="alert">
              <span>Could not load the assembly manifest.</span>
              <button type="button" className="exview__btn" onClick={reload}>Retry</button>
            </div>
          )}
          {!loading && !error && partCount === 0 && (
            <div className="exview__stage-note" role="status">Manifest contained no parts.</div>
          )}

          {!showCard && (
            <p className="exview__hint">
              Click a part to inspect it · drag to orbit · scroll to zoom
            </p>
          )}
        </div>

        <div className="exview__right">
          {/* Icon-only switch. Fixed-height strip so the panel header lines up
              identically whether or not a part is selected. */}
          <div className="exview__tabs" role="tablist" aria-label="Part panel">
            {[
              ['details', 'Details', Info],
              ['drafting', 'Drafting', DraftingCompass],
            ].map(([key, label, icon]) => {
              const TabIcon = icon;
              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={rightTab === key}
                  aria-label={label}
                  title={label}
                  className={`exview__tab${rightTab === key ? ' is-on' : ''}`}
                  onClick={() => setRightTab(key)}
                >
                  <TabIcon size={15} strokeWidth={1.8} aria-hidden="true" />
                </button>
              );
            })}
          </div>
          <div className="exview__tabbody">
            {rightTab === 'drafting' ? (
              <DraftingPanel nodeId={selectedId} tree={tree} manifest={manifest} />
            ) : knowledgeMode && parts ? (
              <PartDossierPanel
                nodeId={selectedId}
                tree={tree}
                parts={parts}
                onSelect={handleSelect}
                onClose={selectedId ? clearSelection : undefined}
                onTrace={startTrace}
                taskRequest={taskRequest}
                domain={asset.domain}
                knowledgeNote={asset.knowledgeNote}
                examplePart={asset.examplePart}
              />
            ) : (
              <PartDetailPanel
                nodeId={selectedId}
                tree={tree}
                vehicle={activeVehicle}
                fetchNodeDetail={fetchNodeDetail}
                onClose={selectedId ? clearSelection : undefined}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
